import moment from 'moment';
import { VICTIM } from 'containers/data-structure';
import { FEATURES } from 'features/features';
import * as featureService from 'features/fflip-helpers';
import currentUserStorage from 'containers/services/security/current-user-storage';
import {
  STATUS_PENDING,
  STATUS_REJECTED,
  STATUS_VALIDATED,
} from 'containers/LawyerFeeNote/constants';
import currentIntervenantStorage from 'containers/services/security/current-intervenant-storage';
import { get, isEmpty, isNil } from 'lodash';
import { PROCEDURE_TYPES } from 'containers/LawyerLegalProceedingsPage/constants';
import { CURRENT_JUDICIARY_STEP_EXPERT_REPORT } from 'containers/CourtManagerLegalProceedings/ExpertReportAppeal/constants';
import { getVictimAge } from 'containers/CourtManagerLegalProceedings/ExpertReport/utils';
import { MODE } from 'containers/CourtManagerLegalProceedings/ExpertReport/constants';
import { EXPERTISE_ACCOUNTING_ID } from 'containers/CourtManagerLegalProceedings/constants';
import { VictimType } from 'typings/globalTypes';

export const hasIncomeCertificate = (victims: any) => {
  return victims.some(
    (item: any) => !isEmpty(get(item, 'incomeCertificate', {})),
  );
};
export const getIncomeCertificateVictims = (victims: any) => {
  return victims.filter(
    (item: any) => !isNil(get(item, VICTIM.ANNUAL_INCOME, null)),
  );
};
export const formatIncomeCertificateVictims = (
  victims: any,
  incomeCertificates: any,
) => {
  const incomeCertificateVictims = Object.values(incomeCertificates).filter(
    (item: any) =>
      !isNil(get(item, 'income', null)) && !isEmpty(get(item, 'victim', {})),
  );
  return incomeCertificateVictims.map((item: any) => {
    const victim = victims.find((el: any) => el.id === item.victimId);
    return { ...item, victim };
  });
};
export const getVictimsWithoutIncomeCertificates = (victims: any) => {
  return victims.filter((item: any) =>
    isEmpty(get(item, 'incomeCertificate', {})),
  );
};

export const getTheLatestEvaluationObject = (
  victimKey: string = '',
  dahirAmounts: any = [],
) => {
  const evaluations = dahirAmounts
    .map((item: any) => {
      return String(item.victimKey) === String(victimKey)
        ? {
            ...item,
            evaluationDate: item.evaluationDate,
            rcAmount: item.rcAmount,
          }
        : null;
    })
    .filter((item: any) => !isEmpty(item));
  const result = !isEmpty(evaluations)
    ? evaluations.reduce((previous: any = {}, current: any = {}) => {
        return get(previous, 'evaluationDate', '') >
          get(current, 'evaluationDate', '')
          ? previous
          : current;
      })
    : {};

  return result;
};

export const formatCerificateToAdd = (
  incomeCertificates: any,
  isManager: any,
  judiciaryProcessId: any,
  claimId: number,
  claimNumber: string,
  riskKey: string,
  victims: any,
  dahirAmounts: any,
  claim: any,
) => {
  const sagisEvaluations = isManager
    ? Object.values(incomeCertificates)
        .filter((item: any) => !item?.id)
        .map((el: any) => {
          const victim = victims.find((it: any) => it.id === el.victimId);
          const hasValidatedAccountinExpertise = !isEmpty(
            get(victim, 'victimExpertise', []).find(
              (element: any) =>
                ![STATUS_PENDING, STATUS_REJECTED].includes(element.status) &&
                element.expertiseId === EXPERTISE_ACCOUNTING_ID,
            ),
          );
          const lastEvaluation = getTheLatestEvaluationObject(
            get(victim, 'victimKey'),
            dahirAmounts,
          );
          if (
            !isEmpty(victim) &&
            !hasValidatedAccountinExpertise &&
            !isEmpty(lastEvaluation)
          ) {
            return {
              gestionMode: '',
              materialAmount: '',
              currentJudiciaryStep: CURRENT_JUDICIARY_STEP_EXPERT_REPORT,
              judiciaryAmount: '',
              responsibilityRate: String(lastEvaluation.responsibilityRate),
              age: getVictimAge(
                get(victim, 'id'),
                victims,
                get(claim, 'claim.lossDate'),
              ),
              rateRecovery: String(lastEvaluation.rateRecovery),
              revenu: parseInt(get(el, 'income')),
              lossDate: moment(get(claim, 'claim.lossDate')).toISOString(true),
              mode:
                get(victim, 'injuryPostKey') === '2'
                  ? MODE.DECEASED
                  : MODE.BLESSE,
              ratePretium: String(lastEvaluation.ratePretium),
              rateAestheticDamageWithoutConsequence: String(
                lastEvaluation.rateAestheticDamageWithoutConsequence,
              ),
              rateAestheticDamageWithConsequence: String(
                lastEvaluation.rateAestheticDamageWithConsequence,
              ),
              rateCareerPrejudice: String(lastEvaluation.rateCareerPrejudice),
              rateSchoolingPrejudice: String(
                lastEvaluation.rateSchoolingPrejudice,
              ),
              rateIPP: String(lastEvaluation.rateIPP),
              rateITT: String(lastEvaluation.rateITT),
              victimKey: get(victim, 'victimKey'),
              medicalPharmaceuticalFees: String(
                lastEvaluation.medicalPharmaceuticalFees,
              ),
              rateMedicalPharmaceuticalFees: String(
                lastEvaluation.rateMedicalPharmaceuticalFees,
              ),
              transactionPhase: 1,
              JudiciartDecisionDate: '',
              expType: '',
              isRecentExpertiseVictim: false,
            };
          } else if (
            !isEmpty(victim) &&
            !hasValidatedAccountinExpertise &&
            isEmpty(lastEvaluation)
          ) {
            return {
              gestionMode: '',
              materialAmount: '',
              currentJudiciaryStep: CURRENT_JUDICIARY_STEP_EXPERT_REPORT,
              judiciaryAmount: '',
              responsibilityRate: String(
                get(claim, 'claim.responsibilityShare', null),
              ),
              rateRecovery: '',
              revenu: get(el, 'income'),
              age: getVictimAge(
                get(victim, 'id'),
                victims,
                get(claim, 'claim.lossDate'),
              ),
              mode:
                get(victim, 'injuryPostKey') === '2'
                  ? MODE.DECEASED
                  : MODE.BLESSE,
              ratePretium: '',
              lossDate: moment(get(claim, 'claim.lossDate')).toISOString(true),
              rateAestheticDamageWithoutConsequence: '',
              rateAestheticDamageWithConsequence: '',
              rateCareerPrejudice: '',
              rateSchoolingPrejudice: '',
              rateIPP: '',
              rateITT: get(victim, 'itt'),
              victimKey: get(victim, 'victimKey'),
              medicalPharmaceuticalFees: '',
              rateMedicalPharmaceuticalFees: '',
              transactionPhase: 1,
              JudiciartDecisionDate: '',
              expType: '',
              isRecentExpertiseVictim: false,
            };
          } else {
            return {};
          }
        })
        .filter((it: any) => !isEmpty(it))
    : [];
  return {
    incomeCertificates: Object.values(incomeCertificates)
      .filter((item: any) => !item?.id)
      .map((item: any) => ({
        ...item,
        lawyerCode: !isManager
          ? currentIntervenantStorage.get() &&
            currentIntervenantStorage.get().sagis_code
          : undefined,
        status: isManager ? STATUS_VALIDATED : STATUS_PENDING,
      })),
    isInboxesActive: featureService.hasFeature(
      FEATURES.ATTRIBUTION_INBOXES_MANAGER,
    ),
    isTrackingActive: featureService.hasFeature(FEATURES.TRACKING_ACTIONS_USER),
    judiciaryProcessId: judiciaryProcessId,
    procedureType: PROCEDURE_TYPES.FIRST_INSTANCE,
    claimId: claimId,
    claimNumber: claimNumber,
    isLawyer: !isManager,
    sagisEvaluations: sagisEvaluations,
    provisionBody:
      !isEmpty(sagisEvaluations) && riskKey
        ? {
            riskID: riskKey,
            date: moment().toISOString(true),
          }
        : {},
    createdBy: isManager
      ? currentUserStorage.get().username
      : currentIntervenantStorage.get().preferred_username,
  };
};

export const getProposedIncomeByProfession = (
  allocation: any,
  victim: VictimType,
  lossDate: Date,
) => {
  const intervals = get(
    allocation?.find(
      (el: { classe: string; coefficient: string; amounts: [] }) =>
        el.classe === get(victim, 'professionCode'),
    ),
    'amounts',
    [],
  );

  const sorted = [...intervals].sort(
    (a, b) =>
      new Date(a.effectiveDate).getTime() - new Date(b.effectiveDate).getTime(),
  );

  const filtered = sorted.filter(
    item => new Date(item.effectiveDate) <= new Date(lossDate),
  );
  const amount = filtered.length > 0 ? filtered[filtered.length - 1] : [];
  return amount?.amount ?? null;
};
